import { GerArquivosService } from "../../../../services/gerArquivosService";
import { runGerArquivosSavePipeline } from "../../../../lib/corel/pipeline/gerArquivosSave";
import { requestConfirmation } from "../../../../lib/globalConfirm";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { LogCategory } from "../../../../config/logging";
import {
	buildPreOrcamentoDirFromParams,
	buscarRegraPorCategoria,
	generateSafeFileName,
} from "../../../../utils/fileNaming";
import type { Cliente } from "../../../../types/gerArquivos";
import type { OrderObject, ProductRule } from "../../types";
import type { Quality } from "../components/QualitySelectModal";
import type { CreatedQuotationOrderData } from "../components/summary/QuotationSummary.types";

export interface QuotationQuickSaveParams {
	createdOrderData: CreatedQuotationOrderData;
	selectedClient: Cliente;
	selectedRule: ProductRule;
	selectedQuality: Quality | null;
	objects: OrderObject[];
	userId: number;
	userName: string;
}

export interface QuotationQuickSaveResult {
	success: boolean;
	error?: string;
	caminhoSalvo?: string;
}

function normalizeWhatsappDigits(client: Cliente): string {
	return (client.whatsapp || "").replace(/\D/g, "");
}

export async function executeQuotationQuickSave(
	params: QuotationQuickSaveParams,
): Promise<QuotationQuickSaveResult> {
	const {
		createdOrderData,
		selectedClient,
		selectedRule,
		selectedQuality,
		objects,
		userId,
		userName,
	} = params;

	if (!createdOrderData.oitId) {
		return {
			success: false,
			error: "Item do orcamento ainda nao disponivel. Aguarde e tente novamente.",
		};
	}

	const clienteId = Number(selectedClient.id);
	if (!Number.isFinite(clienteId) || clienteId <= 0) {
		return {
			success: false,
			error: "Cliente cadastrado obrigatorio para salvar no Gerenciador.",
		};
	}

	const categoriaItem = (selectedRule.produto?.categoria_item ?? 1) as 1 | 2 | 3 | 4;
	const orcId = createdOrderData.orcId;

	try {
		// Checagem de duplicidade: best-effort. Se falhar por erro transiente
		// (ex.: cache/304), não bloqueia o salvamento — apenas assume que não
		// há arquivo prévio. A confirmação de substituição é um aviso, não um gate.
		let existingCount = 0;
		try {
			const existing = await GerArquivosService.buscarArquivos(
				{
					searchTerm: "",
					dateFrom: "",
					dateTo: "",
					dateFilterEnabled: false,
					orcamento_id: orcId,
				},
				{ paginaAtual: 1, itensPorPagina: 1 },
			);
			existingCount = existing.data.arquivos.length;
		} catch (dupError) {
			const dupMessage =
				dupError instanceof Error ? dupError.message : String(dupError);
			sendToTerminal(
				"[QUOTATION:QUICK_SAVE] Checagem de duplicidade falhou — prosseguindo",
				{ orcId, error: dupMessage },
				LogCategory.BUSINESS,
			);
		}

		if (existingCount > 0) {
			const confirmed = await requestConfirmation({
				title: "Arquivo pre existente",
				message: `Ja existe arquivo pre para orcamento #${orcId}. Substituir?`,
				confirmLabel: "Substituir",
				cancelLabel: "Cancelar",
			});

			if (!confirmed) {
				return {
					success: false,
					error: "Salvamento cancelado — arquivo pre ja existe para este orcamento.",
				};
			}
		}

		const regra = await buscarRegraPorCategoria(categoriaItem);
		const diretorioBaseGerenciar = regra.diretorio_base_gerenciar;
		if (!diretorioBaseGerenciar?.trim()) {
			throw new Error(
				`diretorio_base_gerenciar nao encontrado para categoria ${categoriaItem}`,
			);
		}

		const cliWhatsapp = normalizeWhatsappDigits(selectedClient);
		const diretorio = buildPreOrcamentoDirFromParams({
			baseDir: diretorioBaseGerenciar,
			userId,
			userName,
			cliWhatsapp,
			hasCadastro: true,
			clienteId,
			clienteNomeFantasia:
				selectedClient.empresa || selectedClient.nome || createdOrderData.clientName,
			categoriaId: categoriaItem,
		});

		const nomeArquivo = generateSafeFileName(
			{
				orc_id: createdOrderData.orcId,
				oit_id: createdOrderData.oitId,
				cliente: {
					id: clienteId,
					nome: selectedClient.nome,
					nome_fantasia: selectedClient.empresa || selectedClient.nome,
				},
				produto: {
					nome: selectedRule.produto?.nome_servico || createdOrderData.productName,
				},
				material: {
					nome: selectedQuality?.one1_nome || "Material",
				},
			},
			objects.length,
			"AUTO",
		);

		sendToTerminal(
			"[QUOTATION:QUICK_SAVE] Iniciando pipeline rapido",
			{
				orcId,
				oitId: createdOrderData.oitId,
				diretorio,
				nomeArquivo,
			},
			LogCategory.BUSINESS,
		);

		const pipeRes = await runGerArquivosSavePipeline({
			nomeArquivo,
			diretorio,
			prod_cat_item: categoriaItem,
			observacoes:
				createdOrderData.observacaoPedido?.trim() ||
				`Orcamento rapido #${orcId}`,
			cliente_id: String(clienteId),
			apiMode: "simple",
			orcamentoId: orcId,
			oitId: createdOrderData.oitId,
		});

		if (pipeRes.status !== "success" || !pipeRes.data?.caminhoSalvo) {
			throw new Error(pipeRes.error?.message || "Falha no pipeline de salvamento rapido");
		}

		return {
			success: true,
			caminhoSalvo: pipeRes.data.caminhoSalvo,
		};
	} catch (err) {
		const errorMessage =
			err instanceof Error ? err.message : "Falha ao salvar no Gerenciador";
		sendToTerminal(
			"[QUOTATION:QUICK_SAVE] Erro",
			{ orcId, error: errorMessage },
			LogCategory.ERROR,
		);
		return { success: false, error: errorMessage };
	}
}
